import { useEffect } from 'react';
import { ApiService, UserType } from '@/shared/services/api.service';
import { useGuestStore } from '@host/stores/guest.store';
import { useGuestWorkplaceStore } from '@host/guest/stores/guest-workplace.store';
import { isCurrentUserGuest, forceGuestLogoutRedirect } from '@/utils/guestLogoutUtils';
import { UserStatusChangedEvent, GuestLoggedOutEvent, createEventEnvelope } from 'nexgen-types';
import { GlobalEventService } from '@host/services/event.service';
import { Logger } from '../../../shared/services/logger.service';
import { closeGuestWebexMeetingWindow } from '@/apps/webexMeet/components/MeetingLink';
import { sendBeacon } from '@host/services/activity-clean-exit.service';
import {
  AuditoriumGuestContext,
  readAuditoriumContext,
} from '@/apps/auditorium/services/auditorium-lobby';

// Value of the guestUserNotCompletedLogout flag when an auditorium guest left the tab. The
// load path then never gives them a lobby presence, where they would show up to the host
// as an ordinary guest.
const AUDITORIUM_UNLOAD_MARK = 'auditorium';

export async function handleGuestLifecycleLoad() {
  const guestUserNotCompletedLogout = localStorage.getItem('guestUserNotCompletedLogout');
  // Only proceed if current user is guest and logout not completed
  if (!isCurrentUserGuest() || !guestUserNotCompletedLogout) return;

  // Read session state
  const isGuestSessionActive = sessionStorage.getItem('isGuestSessionActive');
  const guestSessionDataStr = sessionStorage.getItem('guestSessionData');
  const guestVisitDataStr = sessionStorage.getItem('guestVisitData');
  const guestPresenceDataStr = sessionStorage.getItem('guestPresenceData');

  const {
    setGuestInfo,
    setVisitDetails,
    setLobbyDetails,
    guestLoginVisit,
    resetGuest,
    setGuestPresence,
  } = useGuestStore.getState();

  // Path 1: No session data — force logout and redirect
  if (!isGuestSessionActive || !guestSessionDataStr) {
    Logger.debug('[GuestLifecycle] 🔄 No session data found, triggering automatic logout');

    const { guestPid, orgPid, hostPid } = readGuestLocalStore();
    Logger.debug('[GuestLifecycle] 🔄 Triggering guest logout via API...', { guestPid, orgPid });

    await performForcedGuestLogout(
      guestPid,
      orgPid,
      hostPid,
      guestLoginVisit,
      resetGuest,
      guestUserNotCompletedLogout === AUDITORIUM_UNLOAD_MARK
    );
    return;
  }

  // Path 2: Session data exists — restore session and presence
  try {
    const guestSessionData = JSON.parse(guestSessionDataStr);
    Logger.debug('[GuestLifecycle] 🔄 Page reload detected, restoring guest session...');

    setGuestInfo(guestSessionData);

    if (guestVisitDataStr) {
      const visitData = JSON.parse(guestVisitDataStr);
      if (visitData.visitDetails) setVisitDetails(visitData.visitDetails);
      if (visitData.lobbyDetails) setLobbyDetails(visitData.lobbyDetails);
    }

    const auditorium = readAuditoriumContext();
    const resp = await guestLoginVisit(auditorium?.roomPid ? { skipPresence: true } : undefined);

    if (resp?.success) {
      Logger.debug('[GuestLifecycle] ✅ Guest session restored successfully');
      if (auditorium?.roomPid) {
        // Tab close removed the auditorium presence; put it back unless the guest is
        // still waiting for the session (no presence while waiting).
        if (!auditorium.waiting) {
          const presenceData = { room_pid: auditorium.roomPid, floor_pid: auditorium.floorPid };
          await restoreGuestPresence(
            resp,
            guestSessionData,
            JSON.stringify(presenceData),
            setGuestPresence
          );
        }
      } else if (guestPresenceDataStr) {
        await restoreGuestPresence(resp, guestSessionData, guestPresenceDataStr, setGuestPresence);
      }
    } else {
      console.error('[GuestLifecycle] ❌ Failed to restore guest session, logging out');
      resetGuest();
      window.location.reload();
    }
  } catch (error) {
    console.error('[GuestLifecycle] ❌ Error restoring guest session:', error);
    resetGuest();
  }
}

export function handleGuestLifecycleBeforeUnload() {
  if (!isCurrentUserGuest()) return;

  // Close the Webex meeting popup (if open) so the guest's media session ends
  // with the tab instead of lingering as a frozen tile for other participants.
  closeGuestWebexMeetingWindow();

  const auditorium = readAuditoriumContext();
  if (auditorium?.roomPid) {
    leaveAuditoriumOnUnload(auditorium);
    return;
  }

  const data = buildBeaconData();

  if (!data) {
    console.warn('[GuestLifecycle] ⚠️ Missing required data for presence beacon');
    return;
  }

  const { guestPid, hostPid, floorPid, roomPid } = data;

  const event = GuestLoggedOutEvent.create({
    guest_pid: guestPid,
    host_pid: hostPid,
    floor_pid: floorPid,
    room_pid: roomPid,
  });
  GlobalEventService.emitTyped(event, true);

  try {
    Logger.debug('[GuestLifecycle] 📤 Updating guest presence via beacon...');
    ApiService.postBeacon(
      '/workplace/guest/presence/self',
      { host_pid: hostPid, to_lobby: true },
      'PATCH'
    );
    Logger.debug('[GuestLifecycle] ✅ Beacon sent successfully');
    localStorage.setItem('guestUserNotCompletedLogout', 'true');
  } catch (error) {
    console.error('[GuestLifecycle] ❌ Failed to send beacon:', error);
  }
}

export function useGuestLifecycle() {
  // Emit logout event and send presence beacon when tab/browser closes
  useEffect(() => {
    window.addEventListener('beforeunload', handleGuestLifecycleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleGuestLifecycleBeforeUnload);
  }, []);

  // Restore or cleanly log out guest session on page load
  useEffect(() => {
    window.addEventListener('load', handleGuestLifecycleLoad);
    return () => window.removeEventListener('load', handleGuestLifecycleLoad);
  }, []);
}

/**
 * Tab close for an auditorium guest: no to_lobby beacon (that would land them in the host's
 * lobby as an ordinary guest). Their auditorium presence is removed right away and the floor
 * is told they left, so they vanish from the floor map instead of lingering for the socket
 * disconnect grace period. Both go out as beacons, which survive the page teardown.
 */
function leaveAuditoriumOnUnload(auditorium: AuditoriumGuestContext): void {
  localStorage.setItem('guestUserNotCompletedLogout', AUDITORIUM_UNLOAD_MARK);

  // A waiting guest has no presence and is on no floor: nothing to remove.
  if (auditorium.waiting) return;

  try {
    ApiService.postBeacon('/workplace/guest/presence/self/auditorium', {}, 'DELETE');

    const guestPid = useGuestStore.getState().guest?.pid;
    const hostPid = useGuestStore.getState().visitDetails?.host_pid;
    if (!guestPid || !hostPid) return;

    const event = GuestLoggedOutEvent.create({
      guest_pid: guestPid,
      host_pid: hostPid,
      floor_pid: auditorium.floorPid,
      room_pid: auditorium.roomPid,
    });
    sendBeacon(createEventEnvelope(event, { user_pid: guestPid }));
  } catch (error) {
    console.error('[GuestLifecycle] ❌ Failed to send auditorium leave beacons:', error);
  }
}

function readGuestLocalStore(): { guestPid: string; orgPid: string; hostPid: string } {
  try {
    const raw = localStorage.getItem('guest-store');
    if (!raw) return { guestPid: '', orgPid: '', hostPid: '' };
    const state = JSON.parse(raw).state;
    return {
      guestPid: state?.guest?.pid ?? '',
      orgPid: state?.visitDetails?.organization_pid ?? state?.guest?.organization_pid ?? '',
      hostPid: state?.visitDetails?.host_pid ?? '',
    };
  } catch (e) {
    Logger.debug('[GuestLifecycle] Failed to read or parse guest-store from localStorage', e);
    return { guestPid: '', orgPid: '', hostPid: '' };
  }
}

function buildBeaconData(): {
  guestPid: string;
  hostPid: string;
  floorPid: string;
  roomPid: string;
} | null {
  const guestWorkplaceStore = useGuestWorkplaceStore.getState();
  const guestStore = useGuestStore.getState();

  const floorPid =
    guestStore.lobbyDetails?.floor_pid || guestWorkplaceStore.currentFloor?.pid || '';
  const roomPid = guestStore.lobbyDetails?.pid || guestWorkplaceStore.currentRoom?.pid || '';
  const guestPid = guestStore.guest?.pid;
  const hostPid = guestStore.visitDetails?.host_pid;

  if (!hostPid || !guestPid) return null;

  return { guestPid, hostPid, floorPid, roomPid };
}

async function performForcedGuestLogout(
  guestPid: string,
  orgPid: string,
  hostPid: string,
  guestLoginVisit: (options?: { skipPresence?: boolean }) => Promise<any>,
  resetGuest: () => void,
  wasAuditoriumGuest = false
): Promise<void> {
  // Remove flag unconditionally so a missing guestPid or API failure
  // doesn't leave it set and cause an infinite redirect loop on /welcome.
  localStorage.removeItem('guestUserNotCompletedLogout');

  if (guestPid) {
    try {
      // An auditorium guest is being logged out: don't hand them a lobby presence on the way.
      const resp = await guestLoginVisit(wasAuditoriumGuest ? { skipPresence: true } : undefined);

      const logoffPayload = {
        guest_pid: guestPid,
        host_pid: hostPid,
        floor_pid: resp?.data?.lobby?.floor_pid || resp?.lobby?.floor_pid,
        room_pid: resp?.data?.lobby?.pid || resp?.lobby?.pid,
      };

      await ApiService.request({
        method: 'POST',
        endpoint: 'welcome/guest/logout',
        data: { guest_pid: guestPid },
        userType: UserType.GuestUser,
        config: { headers: { 'x-org-pid': orgPid } },
      });

      const event = GuestLoggedOutEvent.create(logoffPayload);
      GlobalEventService.emitTyped(event, true);
      Logger.debug('[GuestLifecycle] ✅ Guest logout API call successful');
    } catch (e) {
      console.error('[GuestLifecycle] ❌ Guest logout API call failed:', e);
    }
  }

  resetGuest();
  // Shared with the manual and host-initiated logouts: clears the guest localStorage and
  // lands on the guest login form with the member auto-login sat out.
  forceGuestLogoutRedirect();
}

async function restoreGuestPresence(
  resp: any,
  guestSessionData: any,
  guestPresenceDataStr: string,
  setGuestPresence: (data: any) => void
): Promise<void> {
  const presenceData = JSON.parse(guestPresenceDataStr);
  if (!presenceData.room_pid) return;

  Logger.debug('[GuestLifecycle] 📍 Restoring guest presence in room:', presenceData.room_pid);
  setGuestPresence(presenceData);

  try {
    await ApiService.patch('/workplace/guest/presence/self', {
      host_pid: resp.data.visit.host_pid,
      room_pid: presenceData.room_pid,
      to_lobby: false,
    });
    Logger.debug('[GuestLifecycle] ✅ Guest presence restored on backend');

    const userStatusEvent = UserStatusChangedEvent.create({
      user_pid: guestSessionData.pid,
      room_pid: presenceData.room_pid,
      floor_pid: presenceData.floor_pid,
      status: 'available',
    });
    GlobalEventService.emitTyped(userStatusEvent, true);
    Logger.debug('[GuestLifecycle] 📡 Presence update event emitted');
  } catch (presenceError) {
    console.error('[GuestLifecycle] ❌ Failed to restore guest presence:', presenceError);
  }
}
